import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DesignAngularKitModule } from 'design-angular-kit';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import {
  Actor,
  ActorKey,
  AslDetails,
  ContextService,
  ContextState,
} from '../../services/context.service';
import { UserService } from '../../services/user.service';

@Component({
  standalone: true,
  selector: 'app-context',
  imports: [CommonModule, DesignAngularKitModule],
  templateUrl: './context.component.html',
  styleUrl: './context.component.scss',
})
export class ContextComponent implements OnInit, OnDestroy {
  constructor(
    private readonly router: Router,
    public readonly authService: AuthService,
    public readonly context: ContextService,
    private readonly keycloakService: KeycloakService,
    private readonly userService: UserService,
    private route: ActivatedRoute,
  ) {
    this.route.queryParams.subscribe((params) => {
      if (params && params['patientFiscalCode']) {
        this.context.patientFiscalCode = params['patientFiscalCode'];
      }
    });
    this.state$ = this.context.state$;
    this.vm$ = this.state$.pipe(
      map((state) => {
        const keys: ActorKey[] = ['cot', 'uvm', 'pua', 'bm', 'ward', 'mmg'];
        const visibleKeys = keys.filter((k) => (state[k].items?.length ?? 0) > 0);
        const counts = keys.map((k) => state[k].items?.length ?? 0);
        const total = counts.reduce((a, b) => a + b, 0);
        const nonEmpty = keys.filter((k) => (state[k].items?.length ?? 0) > 0);
        return { state, visibleKeys, total, nonEmpty };
      })
    );
  }

  readonly state$!: Observable<ContextState>;
  readonly vm$!: Observable<{ state: ContextState; visibleKeys: ActorKey[]; total: number; nonEmpty: ActorKey[] }>;
  readonly keys: ActorKey[] = ['cot', 'uvm', 'pua', 'bm', 'ward', 'mmg'];

  message = 'Seleziona il contesto con cui operare';
  aslDetails: Pick<AslDetails, 'code' | 'name'> | null = null;

  private sub?: Subscription;
  private autoNavigated = false;

  async ngOnInit(): Promise<void> {
    const profile = await this.keycloakService.loadUserProfile();
    const account = await firstValueFrom(this.userService.insertUserFromAccount());
    const aslCodes: string[] =
      Array.isArray(profile.attributes?.['ASL']) &&
      typeof profile.attributes?.['ASL'][0] === 'string'
        ? profile.attributes?.['ASL'][0].split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    if (aslCodes.length === 0) {
      this.router.navigate(['/context']);
      return;
    }

    const aslCode = aslCodes[0];
    this.context.bootstrapFromAslDetails(aslCode).subscribe((asl) => {
      this.aslDetails = asl ? { code: asl.code, name: asl.name } : null;
    });
    const currentState = await firstValueFrom(this.state$);

    this.sub = this.vm$.subscribe(({ state, visibleKeys, total, nonEmpty }) => {
      if (this.autoNavigated) return;
      if (total === 0 || visibleKeys.length === 0) return; // not ready

      const preSelKey = this.keys.find((k) => !!state[k].selected) || null;
      if (preSelKey) {
        if(this.context.patientFiscalCode) {
          this.router.navigate(['/dashboard-mmg']);
        } else {
          this.router.navigate(['/dashboard']);
        }
        this.autoNavigated = true;
        return;
      }

      if (total === 1) {
        const { key, item } = this.pickFirstItem(state);
        if (key && item) return this.selectAndGo(key, item);
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  onSelect(key: ActorKey, item: Actor) {
    this.selectAndGo(key, item);
  }

  private selectAndGo(key: ActorKey, item: Actor) {
    this.context.select(key, item);
    this.autoNavigated = true;
    if(this.context.patientFiscalCode) {
      this.router.navigate(['/dashboard-mmg']);
    } else {
      this.router.navigate(['/dashboard']);
    }
  }

  private pickFirstItem(state: ContextState): { key: ActorKey | null; item: Actor | null } {
    for (const k of this.keys) {
      const arr = state[k].items as Actor[];
      if (arr?.length) return { key: k, item: arr[0] };
    }
    return { key: null, item: null };
  }
}
